import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Star } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { API_URL, API_ORIGIN } from "../config";

/* ===== Price UI ===== */
function PriceView({ base, promo, currency="ARS" }) {
  if (base == null && promo == null) return <span>Consultar</span>;
  if (promo != null) {
    return (
      <>
        <span className="line-through opacity-60 mr-2">${base.toLocaleString("es-AR")}</span>
        <span className="text-pink-600">${promo.toLocaleString("es-AR")}</span>
        <span className="ml-1 text-xs opacity-70">{currency}</span>
      </>
    );
  }
  return (
    <>
      <span className="text-pink-600">${base.toLocaleString("es-AR")}</span>
      <span className="ml-1 text-xs opacity-70">{currency}</span>
    </>
  );
}

const PopularProducts = () => {
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/products.php`);
        if (!resp.ok) throw new Error("API error");
        const data = await resp.json();
        
        // Filtramos destacados o los 3 primeros
        const featured = data.filter(p => p.featured);
        setItems((featured.length ? featured : data).slice(0, 3));
      } catch (e) {
        console.error("popular fetch error", e);
        setItems([]);
      }
    })();
  }, []);

  const handleAdd = (p) => {
    // Tomamos variante 18 por defecto o la primera que haya
    const v = p.variants?.find(v => v.key === "18") || p.variants?.[0];
    if (!v) return;
    
    const finalPrice = v.price_promo ?? v.price_base;
    if (finalPrice == null) return;

    const detail = [p.detail, v.key === "default" ? "" : `Tamaño ${v.key} cm`]
      .filter(Boolean).join(" • ");

    addToCart({
      id: `${p.id}-${v.key}`,
      productId: p.id,
      name: p.name,
      detail,
      image: p.image?.startsWith('/') ? `${API_ORIGIN}${p.image}` : p.image,
      currency: p.currency || "ARS",
      price: Number(finalPrice),
      variant: v.key,
    });
  };

  if (!items.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-12 bg-white/50"
    >
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Los Más Solicitados
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {items.map((product, index) => {
            const v = product.variants?.find(v => v.key === "18") || product.variants?.[0];

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.04 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col"
              >
                <img
                  src={product.image?.startsWith('/') ? `${API_ORIGIN}${product.image}` : (product.image || '/placeholder.jpg')}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                  onError={(e) => { 
                    if (!e.target.dataset.error) {
                      e.target.dataset.error = "true";
                      e.target.src = 'https://placehold.co/600x400?text=GK+Kitchen';
                    }
                  }}
                />
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-yellow-600">4.8</span>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 text-sm line-clamp-2">{product.detail}</p>

                  <div className="flex justify-between items-center mt-auto">
                    <span className="text-xl font-bold">
                      <PriceView
                        base={v?.price_base ?? null}
                        promo={v?.price_promo ?? null}
                        currency={product.currency}
                      />
                    </span>
                    <button
                      onClick={() => handleAdd(product)}
                      className="flex items-center space-x-1 text-pink-500 hover:text-pink-600 transition"
                    >
                      <Heart className="w-5 h-5" />
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

export default PopularProducts;
